import Vue from 'vue'

import type {
  Undefined,
  Nullable,
  Pagination,
  Paginator,
  Room,
  RoomActivity,
  RoomActivityGroup,
  ActivityId,
  ActivityGroupActivityId,
  RoomId,
  RoomActivityId,
  RoomActivityGroupId,
} from '@/types'
import { getCurrentInstance, mountVueToBody } from '@/util'
import {
  useActivityService,
  useRoomService,
  useConfirm,
  usePrompt,
  useLoading,
  useDialogify,
} from '@/use'
import DialogPagination from '@/components/DialogPagination/DialogPagination.vue'

export default function useRoomCRUD() {
  const instance = getCurrentInstance()
  const h = instance.proxy.$createElement
  const { service: activityService } = useActivityService()
  const { service: roomService } = useRoomService()
  const { confirm } = useConfirm()
  const { prompt } = usePrompt()
  const { setLoading } = useLoading()
  const { dialogify } = useDialogify()

  async function create(): Promise<Nullable<Room>> {
    const { default: options } = await import(
      '@/components/RoomForm/CreateRoomForm/CreateRoomFormDialog.vue'
    )
    return new Promise((resolve) => {
      const c = new Vue({
        extends: options,
        parent: instance.proxy.$root,
      })
      const destroy = mountVueToBody(c)
      c.$once('success', (room: Room) => {
        resolve(room)
        destroy()
      })
      c.$once('fail', () => {
        resolve(null)
        destroy()
      })
    })
  }

  async function copy(id: RoomId, initial: string): Promise<Undefined<RoomId>> {
    const name = await prompt({
      title: 'New Weve Name',
      label: 'Name',
      initial,
    })
    if (name === undefined) {
      return undefined
    }
    setLoading(true)
    try {
      const result = await roomService.copy(id, name)
      return result
    } finally {
      setLoading(false)
    }
  }

  async function del(id: RoomId): Promise<Nullable<RoomId>> {
    if (
      (await confirm('Are you sure you would like to delete this game?')) ===
      false
    ) {
      return null
    }
    return roomService.delete(id)
  }

  async function editActivity(
    relation: RoomActivityId,
    id: ActivityId,
    paginator: Nullable<Paginator> = null
  ): Promise<Nullable<RoomActivity>> {
    setLoading(true)
    try {
      const activity = await activityService.get(id, { relations: true })
      const { prev, next } = paginator ?? { prev: null, next: null }
      const pagination: Nullable<Pagination> =
        prev !== null || next !== null
          ? { hasPrev: prev !== null, hasNext: next !== null }
          : null
      const { default: options } = await import(
        '@/components/ActivityForm/EditRoomActivityForm/EditRoomActivityForm.vue'
      )
      const EditRoomActivityForm = Vue.extend({ extends: options })
      const updated = await new Promise<Nullable<RoomActivity>>((resolve) => {
        const { destroyer } = dialogify(
          [
            h(EditRoomActivityForm, {
              props: { relation, activity },
              on: {
                success: async (value: RoomActivity) => {
                  resolve(value)
                  destroyer()
                },
                fail: () => {
                  resolve(null)
                  destroyer()
                },
              },
            }),
            pagination !== null
              ? h(DialogPagination, {
                  props: { pagination },
                  on: {
                    next: () => {
                      if (next !== null) {
                        next()
                        destroyer()
                      }
                    },
                    prev: () => {
                      if (prev !== null) {
                        prev()
                        destroyer()
                      }
                    },
                  },
                })
              : null,
          ],
          { title: `Edit Room Activity "${activity.name}"`, size: 'xl' },
          { hide: () => resolve(null) }
        )
      })
      return updated
    } catch (e) {
      window.alert(e)
      return null
    } finally {
      setLoading(false)
    }
  }

  async function addActivityGroupActivity(
    roomActivityGroupId: RoomActivityGroupId,
    activityId: ActivityId,
    position: number
  ) {
    setLoading(true)
    try {
      const roomActivityGroup = roomService.addActivityGroupActivity(
        roomActivityGroupId,
        activityId,
        position
      )
      return roomActivityGroup
    } catch (e) {
      window.alert(e)
      return null
    } finally {
      setLoading(false)
    }
  }

  async function editActivityGroupActivity(
    roomActivityGroupId: RoomActivityGroupId,
    activityGroupActivityId: ActivityGroupActivityId,
    activityId: ActivityId,
    paginator: Nullable<Paginator> = null
  ): Promise<Nullable<RoomActivityGroup>> {
    setLoading(true)
    try {
      const activity = await activityService.get(activityId, {
        relations: true,
      })
      const { prev, next } = paginator ?? { prev: null, next: null }
      const pagination: Nullable<Pagination> =
        prev !== null || next !== null
          ? { hasPrev: prev !== null, hasNext: next !== null }
          : null
      const { default: options } = await import(
        '@/components/ActivityForm/EditRoomActivityGroupActivity/EditRoomActivityGroupActivity.vue'
      )
      const EditRoomActivityGroupActivity = Vue.extend({ extends: options })
      const updatedRoomActivityGroup = await new Promise<
        Nullable<RoomActivityGroup>
      >((resolve) => {
        const { destroyer } = dialogify(
          [
            h(EditRoomActivityGroupActivity, {
              props: {
                roomActivityGroupId,
                activityGroupActivityId,
                activity,
              },
              on: {
                success: async (value: RoomActivityGroup) => {
                  resolve(value)
                  destroyer()
                },
                fail: () => {
                  resolve(null)
                  destroyer()
                },
              },
            }),
            h(DialogPagination, {
              props: { pagination },
              on: {
                next: () => {
                  if (next !== null) {
                    next()
                  }
                  destroyer()
                },
                prev: () => {
                  if (prev !== null) {
                    prev()
                  }
                  destroyer()
                },
              },
            }),
          ],
          {
            title: `Edit Room Activity Group Activity "${activity.name}"`,
            size: 'xl',
          },
          { hide: () => resolve(null) }
        )
      })
      return updatedRoomActivityGroup
    } catch (e) {
      window.alert(e)
      return null
    } finally {
      setLoading(false)
    }
  }

  async function removeActivityGroupActivity(
    roomActivityGroupId: RoomActivityGroupId,
    activityGroupActivityId: ActivityGroupActivityId
  ): Promise<Nullable<RoomActivityGroup>> {
    setLoading(true)
    try {
      const roomActivityGroup = await roomService.removeActivityGroupActivity(
        roomActivityGroupId,
        activityGroupActivityId
      )
      return roomActivityGroup
    } catch (e) {
      window.alert(e)
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    create,
    copy,
    del,
    editActivity,
    addActivityGroupActivity,
    editActivityGroupActivity,
    removeActivityGroupActivity,
  }
}
